import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { COLORS } from '../../pages/app/reports/groups-teams-activity/constants';
import { WeeklyDataPoint } from '../../pages/app/reports/groups-teams-activity/types';

interface WeeklyBarChartProps {
    data: WeeklyDataPoint[];
    title: string;
    subtitle: string;
}

const getBarColor = (point: WeeklyDataPoint): string => {
    if (point.isCurrent) return COLORS.barCurrent;
    if (point.activePercent >= 12) return COLORS.barHigh;
    if (point.activePercent >= 10) return COLORS.barMedium;
    if (point.activePercent >= 7) return COLORS.barDefault;
    return '#BFDBFE';
};

const CustomLabel = (props: any) => {
    const { x, y, width, value, index, data } = props;
    const point = data[index];
    const isCurrent = point?.isCurrent;
    return (
        <text
            x={x + width / 2}
            y={y - 6}
            fill={isCurrent ? COLORS.primary : COLORS.grayDark}
            fontSize={9}
            fontWeight={isCurrent ? 700 : 600}
            textAnchor="middle"
            fontFamily="Poppins, sans-serif"
        >
            {value}%
        </text>
    );
};

export const WeeklyBarChart: React.FC<WeeklyBarChartProps> = ({ data, title, subtitle }) => {
    return (
        <Box
            sx={{
                backgroundColor: COLORS.cardBg,
                borderRadius: '10px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    borderBottom: `1px solid ${COLORS.borderLight}`,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11px', color: COLORS.grayMedium }}>{subtitle}</Typography>
                </Box>
            </Box>

            {/* Chart */}
            <Box sx={{ padding: '16px 18px', height: 150 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 20, right: 4, left: 4, bottom: 0 }}>
                        <XAxis
                            dataKey="weekLabel"
                            axisLine={false}
                            tickLine={false}
                            tick={(props: any) => {
                                const point = data[props.index];
                                return (
                                    <text
                                        x={props.x}
                                        y={props.y + 12}
                                        fill={point?.isCurrent ? COLORS.primary : COLORS.grayMedium}
                                        fontSize={8}
                                        fontWeight={point?.isCurrent ? 600 : 400}
                                        textAnchor="middle"
                                        fontFamily="Poppins, sans-serif"
                                    >
                                        {props.payload.value}
                                    </text>
                                );
                            }}
                        />
                        <YAxis hide />
                        <Bar
                            dataKey="activePercent"
                            radius={[3, 3, 0, 0]}
                            maxBarSize={40}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={index}
                                    fill={getBarColor(entry)}
                                    stroke={entry.isCurrent ? COLORS.blueDark : 'none'}
                                    strokeWidth={entry.isCurrent ? 2 : 0}
                                />
                            ))}
                            <LabelList
                                dataKey="activePercent"
                                content={(props: any) => <CustomLabel {...props} data={data} />}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};
